import { useGenerateMasterPrompt, useGetAiWorkspace } from "@entities/aiWorkspace";
import { Button, Input, Select } from "@shared/ui";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { ChevronDown, Loader2, Sparkles, Wand2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { promptToText } from "../WorkspacePromptEditor/promptToText";
import formStyles from "./conceptForm.module.scss";
import styles from "./editor.module.scss";
import {
  SCRIPT_DURATIONS,
  SCRIPT_FORMATS,
  SCRIPT_LANGUAGES,
  SCRIPT_TONES,
  ScriptDraft,
} from "./types";

export function ScriptOutputFields({
  active,
  aiWorkspaceId,
  generating,
  actionLabel,
  hint,
  onChange,
  onGenerate,
  onBriefBlur,
}: {
  active: ScriptDraft;
  aiWorkspaceId: string;
  generating: boolean;
  actionLabel: string;
  hint: string;
  onChange: (patch: Partial<Pick<ScriptDraft, "brief" | "settings">>) => void;
  onGenerate: () => void;
  onBriefBlur?: () => void;
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const pendingBrief = useRef<string | null>(null);
  const onBriefBlurRef = useRef(onBriefBlur);
  onBriefBlurRef.current = onBriefBlur;
  const { data: workspaceData } = useGetAiWorkspace(aiWorkspaceId);
  const overviewMissing = !promptToText(workspaceData?.data?.overview || "").trim();
  const { generate, isGenerating } = useGenerateMasterPrompt((analysis) => {
    pendingBrief.current = analysis;
    onChange({ brief: analysis });
  });
  const busy = generating || isGenerating;

  useEffect(() => {
    if (pendingBrief.current == null || active.brief !== pendingBrief.current) return;
    pendingBrief.current = null;
    onBriefBlurRef.current?.();
  }, [active.brief]);
  const durationLabel =
    SCRIPT_DURATIONS.find((item) => item.value === active.settings.duration)?.label ||
    active.settings.duration;
  const summary = `${active.settings.format} · ${durationLabel} · ${active.settings.tone} · ${active.settings.language}`;

  return (
    <section className={formStyles.form}>
      <div className={formStyles.formHead}>
        <h3>Brief</h3>
        <p>{hint}</p>
      </div>
      <div className={styles.field}>
        <div className={formStyles.briefHead}>
          <span className={formStyles.briefLabel}>
            Brief
            <span className={formStyles.optional}>Optional</span>
          </span>
          <DisabledPopover
            isDisabled={overviewMissing}
            content="Add a master brief before generating from the overview."
          >
            <button
              type="button"
              className={formStyles.overviewBtn}
              disabled={busy || overviewMissing}
              onClick={() => {
                if (overviewMissing) return;
                generate({ aiWorkspaceId, type: "script-generate" });
              }}
            >
              {isGenerating ? <Loader2 size={14} className="spin-animation" /> : <Wand2 size={14} />}
              {isGenerating ? "Generating" : "Generate from overview"}
            </button>
          </DisabledPopover>
        </div>
        <Input.TextArea
          value={active.brief}
          rows={8}
          disabled={busy}
          placeholder="Objective, product message, creative direction, and mandatories."
          onChange={(event) => onChange({ brief: event.target.value })}
          onBlur={onBriefBlur}
        />
      </div>
      <div className={formStyles.advanced}>
        <button
          type="button"
          className={formStyles.advancedToggle}
          aria-expanded={advancedOpen}
          onClick={() => setAdvancedOpen((open) => !open)}
        >
          <span className={formStyles.advancedTitle}>Advanced</span>
          <span className={formStyles.optional}>Optional</span>
          {advancedOpen ? null : (
            <span className={formStyles.advancedSummary}>{summary}</span>
          )}
          <ChevronDown
            size={16}
            className={`${formStyles.chevron} ${advancedOpen ? formStyles.chevronOpen : ""}`}
          />
        </button>
        {advancedOpen ? (
          <div className={formStyles.settings}>
            <p className={formStyles.settingsMeta}>
              {active.creator.platform === "youtube" ? "YouTube" : "Instagram"} · @
              {active.creator.username}
            </p>
            <label className={styles.field}>
              <span>Format</span>
              <Select
                value={active.settings.format}
                disabled={busy}
                options={SCRIPT_FORMATS.map((format) => ({ value: format, label: format }))}
                onChange={(format) =>
                  onChange({ settings: { ...active.settings, format: String(format) } })
                }
              />
            </label>
            <label className={styles.field}>
              <span>Duration</span>
              <Select
                value={active.settings.duration}
                disabled={busy}
                options={SCRIPT_DURATIONS.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
                onChange={(duration) =>
                  onChange({ settings: { ...active.settings, duration: String(duration) } })
                }
              />
            </label>
            <label className={styles.field}>
              <span>Tone balance</span>
              <Select
                value={active.settings.tone}
                disabled={busy}
                options={SCRIPT_TONES.map((tone) => ({ value: tone, label: tone }))}
                onChange={(tone) =>
                  onChange({ settings: { ...active.settings, tone: String(tone) } })
                }
              />
            </label>
            <label className={styles.field}>
              <span>Language</span>
              <Select
                value={active.settings.language}
                disabled={busy}
                options={SCRIPT_LANGUAGES.map((language) => ({
                  value: language,
                  label: language,
                }))}
                onChange={(language) =>
                  onChange({ settings: { ...active.settings, language: String(language) } })
                }
              />
            </label>
          </div>
        ) : null}
      </div>
      <Button
        type="primary"
        disabled={busy}
        onClick={onGenerate}
        className={`${styles.generateButton} ${formStyles.generate}`}
      >
        {generating ? <Loader2 size={15} className="spin-animation" /> : <Sparkles size={15} />}
        {actionLabel}
      </Button>
    </section>
  );
}
